import { MarkerClusterer } from '@googlemaps/markerclusterer';
import App from "../../app";

interface Position {
    lat: number,
    lng: number,
}

export interface ManufacturerMapLocation {
    id: number,
    name: string
    brands: string
    country: string
    url: string
    thumbnail?: string,
    position: Position
}

export default class ManufacturerMap {
    private readonly elem: HTMLElement;
    private mapElem: HTMLElement;

    private locations: Record<number, ManufacturerMapLocation> = {};
    private markers: Array<google.maps.Marker> = [];

    private currentInfoWindow: google.maps.InfoWindow;

    private map: google.maps.Map = null;

    private mapOptions: google.maps.Options = {};

    public static scriptLoaded = false;

    constructor(elem: HTMLElement, mapOptions: google.maps.Options = {}) {
        this.elem = elem;
        this.mapOptions = mapOptions;

        this.mapOptions = {
            zoom: 6,
            zoomControl: true,
            scaleControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            backgroundColor: '#fffff',
            gestureHandling: 'cooperative',
            styles: [
                {
                    featureType: 'poi',
                    stylers: [{visibility: 'off'}],
                },
            ],
            ...this.mapOptions
        };

        this.init();
    }

    private async init(): void {
        await this.loadScript();
        this.map = new google.maps.Map(this.createMapElem(), this.mapOptions);

        this.update();
    }

    private createMapElem(): HTMLElement {
        this.mapElem = document.createElement('DIV');

        this.mapElem.style.width = '100%';
        this.mapElem.style.height = '100%';

        this.elem.append(this.mapElem);

        return this.mapElem;
    }

    private loadScript(): Promise<void> {
        return new Promise((resolve) => {
            if (ManufacturerMap.scriptLoaded === true) {
                resolve();
                return;
            }

            const script = document.createElement('SCRIPT');
            script.setAttribute('src', 'https://maps.googleapis.com/maps/api/js?key=' + App.getData('google_maps_api_key') + '&language=' + App.getData('lang'));
            script.addEventListener('load', () => {
                ManufacturerMap.scriptLoaded = true;
                resolve();
            });
            document.body.appendChild(script);
        })
    }

    public addLocation(location: ManufacturerMapLocation) {
        if (location.position.lat === null || location.position.lng === null) {
            return;
        }

        this.locations[location.id] = location;
    }

    public update() {
        if (!ManufacturerMap.scriptLoaded || !this.map) {
            return;
        }

        this.updateMarkers();
        this.updateCluster();
        this.center();
    }

    private clusterer: MarkerClusterer;

    private updateCluster() {
        if (!this.map) return;

        if (this.clusterer) {
            this.clusterer.clearMarkers();
        }

        this.clusterer = new MarkerClusterer({
            map: this.map,
            markers: this.markers,
        });
    }

    public renderInfoWindowContent(location: ManufacturerMapLocation): string {
        return `
            <article class="manufacturer-card manufacturer-card--small">
                <div class="manufacturer-card__wrapper">
                        <div class="manufacturer-card__content">
                            <a class="manufacturer-card__content__link" href="${location.url}">
                                ${location.thumbnail ? `<div class="manufacturer-card__logo"><img src="${location.thumbnail}" alt=""></div>` : ``}
                           
                                <h3>${location.name}</h3>
                            </a>
                        </div>
                    <div class="manufacturer-card__footer">
                        <div class="manufacturer-card__footer__product">${location.brands}</div>
                        <div class="manufacturer-card__footer__country">${location.country}</div>
                    </div>
                </div>
          </article>
        `;
    }

    public updateMarkers() {
        this.markers = [];

        Object.keys(this.locations).forEach((id) => {
            const location = this.locations[id];

            const marker = new google.maps.Marker({
                position: new google.maps.LatLng(location.position.lat, location.position.lng),
                animation: google.maps.Animation.DROP,
                clickable: true
            });

            marker.addListener('click', () => {
                if (this.currentInfoWindow) {
                    this.currentInfoWindow.close();
                }

                this.currentInfoWindow = new google.maps.InfoWindow({
                    content: this.renderInfoWindowContent(location)
                });

                this.currentInfoWindow.open({
                    anchor: marker,
                    map: this.map,
                    shouldFocus: true
                });
            });

            this.markers.push(marker);
        });
    }

    public center() {
        const markersLength = Object.keys(this.markers).length;
        if (markersLength <= 0) {
            return;
        }

        if (markersLength === 1) {
            this.map.setCenter(Object.values(this.markers)[0].getPosition());
        } else {
            const bounds = new google.maps.LatLngBounds();
            this.markers.forEach((marker) => bounds.extend(marker.getPosition()));
            this.map.fitBounds(bounds);
        }
    }

    public destroy() {
        this.markers = {};
        this.locations = {};
        this.mapElem.remove();

        this.mapElem = null;
        this.map = null;
    }
}